"use client";

import { createContext, useContext, useEffect, useState } from "react";

/**
 * M3 — Audience-lens system. Lets the same scenario engine surface
 * different evidence layers depending on who's in the room:
 *
 *   external   — default. Brand-safe, narrative-led. The shape the
 *                public demo (demo.orbitralab.com) ships.
 *   banker     — surfaces ISO 20022 message progression, settlement
 *                finality, take-rate per trade, structured-credit
 *                details. Optimized for trade-finance pitches.
 *   investor   — surfaces unit economics: fees collected, take rate,
 *                lifetime customer value, ramp curve assumptions.
 *   regulator  — surfaces compliance evidence detail: sanctions-list
 *                freshness, Travel Rule data flows, audit-trail
 *                anchors, retention policy.
 *   enterprise — surfaces integration surface: API endpoints,
 *                multi-tenant boundaries, data residency, SLAs.
 *
 * Lens is set by URL param `?lens=banker` (etc.). Default is
 * `external` if the param is missing or unrecognized.
 *
 * Implementation note: we deliberately do NOT use Next.js's
 * `useSearchParams` here. In Next.js 16's App Router, calling
 * useSearchParams from a client component that hosts the whole
 * app subtree forces a Suspense boundary, and that boundary's
 * fallback either suppresses child effects (PresenterChrome's
 * /api/scenarios fetch) or double-mounts them — both observed
 * as "scenario catalogue empty" on /demo?lens=X. Reading
 * window.location.search directly in a mount-time effect sidesteps
 * the suspension entirely. The trade-off (initial paint uses
 * "external" before the effect runs) is invisible: every scene
 * component that depends on the lens reads it inside its own
 * render, after the effect has flushed.
 */
export type AudienceLens = "external" | "banker" | "investor" | "regulator" | "enterprise";

const LENS_VALUES: ReadonlyArray<AudienceLens> = [
  "external", "banker", "investor", "regulator", "enterprise",
];

const AudienceLensContext = createContext<AudienceLens>("external");

function resolveLens(search: string): AudienceLens {
  const match = search.match(/[?&]lens=([^&]+)/i);
  if (!match) return "external";
  const candidate = decodeURIComponent(match[1]).toLowerCase();
  return (LENS_VALUES as ReadonlyArray<string>).includes(candidate)
    ? (candidate as AudienceLens)
    : "external";
}

export function AudienceLensProvider({ children }: { children: React.ReactNode }) {
  const [lens, setLens] = useState<AudienceLens>("external");

  useEffect(() => {
    if (typeof window === "undefined") return;
    setLens(resolveLens(window.location.search));
  }, []);

  return <AudienceLensContext.Provider value={lens}>{children}</AudienceLensContext.Provider>;
}

export function useAudienceLens(): AudienceLens {
  return useContext(AudienceLensContext);
}

/** Convenience helper for the most common conditional render pattern. */
export function useIsLens(target: AudienceLens): boolean {
  return useContext(AudienceLensContext) === target;
}
